import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { KpiScale } from 'src/app/models/kpiform';
import { KpiAsset, KpiCycle, KpiFamily } from 'src/app/services/kpi.service';
import ChartBaseComponent from '../../../shared/kpi/chart.base';
import { Ng2ChartConfiguration } from '../../../shared/kpi/chart.model';

enum DatasetColors {
  static = 'orange',
  moving = 'blue',
  unknown = 'grey',
}

@Component({
    selector: 'app-bar-cycle-static',
    templateUrl: '../../../shared/kpi/chart-card-scrollable.component.html',
    styleUrls: ['../../../shared/kpi/chart-card-scrollable.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BarCycleStaticComponent extends ChartBaseComponent<KpiFamily, 'bar', number> implements OnChanges {

  @Input() scale: KpiScale;
  @Input() selectedAssetIndex: number;

  public chartConfiguration: Ng2ChartConfiguration<'bar', number[], number> = {
    type: 'bar',
    data: { datasets: [] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          position: 'top',
        },
        legend: {
          display: true,
          position: 'bottom',
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        x: {
          stacked: true,
          display: true,
          beginAtZero: true,
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
          display: true,
        },
      },
    },
  };

  private _unit: string;

  ngOnChanges(changes: SimpleChanges): void {
    // Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    // Add '${implements OnChanges}' to the class.
    super.ngOnChanges(changes);
    if (changes.scale) {
      if (changes.scale.currentValue !== changes.scale.previousValue) {
        this._handleScaleChange();
        this._cd.markForCheck();
      }
    }
  }

  private _setTitle(asset: KpiAsset) {
    this.chartConfiguration.options.plugins.title.text = `${this._translate.instant('STATIC_TIME_MONITORING')} - ${this._translate.instant('CYCLE_TIME_MONITORING')} (${this._unit}) - ${this._translate.instant('ASSET')} ${asset.assetName}`;
  }

  /**
   * Handle a change of scope
   * Set the values relative to percentage or values
   */
  private _handleScaleChange() {
    this._unit = this.scale === 'percent' ? '%' : this._translate.instant('HOURS');
    this.chartConfiguration.options.scales.y.ticks = this.scale === 'percent' ? { callback(value) { return value + '%'; } } : undefined;
    if (this.dataKPI) {
      this.setKpiData();
    }
  }

  /**
   * Get the percentage of time relative to all times (static + moving + unknown)
   * @param time The time value
   * @returns The percentage
   */
  private _getPercentageOfTime(time: number, cycle: KpiCycle): number {
    const sum = cycle.cycleStaticTime + cycle.cycleMovingTime + cycle.cycleUnknownTime;
    return +(time / sum * 100).toFixed(2);
  }

  setKpiData(): void {
    const listCycles = this.dataKPI.familyAssets[this.selectedAssetIndex].assetCycles;
    this.chartConfiguration.data = {
      labels: listCycles.map(
        cycle => cycle.cycleCycleOrder
      ),
      datasets: [
        {
          label: `${this._translate.instant('STATIC_TIME')} (${this._unit})`,
          backgroundColor: DatasetColors.static,
          data: listCycles.map(
            cycle => this.scale === 'percent' ? this._getPercentageOfTime(cycle.cycleStaticTime, cycle) : cycle.cycleStaticTime
          ),
          borderColor: DatasetColors.static,
          borderWidth: 1,
        },
        {
          label: `${this._translate.instant('MOVING_TIME')} (${this._unit})`,
          backgroundColor: DatasetColors.moving,
          data: listCycles.map(
            cycle => this.scale === 'percent' ? this._getPercentageOfTime(cycle.cycleMovingTime, cycle) : cycle.cycleMovingTime
          ),
          borderColor: DatasetColors.moving,
          borderWidth: 1,
        },
        {
          label: `${this._translate.instant('UNKNOWN_TIME')} (${this._unit})`,
          backgroundColor: DatasetColors.unknown,
          data: listCycles.map(
            cycle => this.scale === 'percent' ? this._getPercentageOfTime(cycle.cycleUnknownTime, cycle) : cycle.cycleUnknownTime
          ),
          borderColor: DatasetColors.unknown,
          borderWidth: 1,
        },
      ],
    };
    this._setTitle(this.dataKPI.familyAssets[this.selectedAssetIndex]);
    this.graphSize = (listCycles.length > 30) ? `${60 * listCycles.length}px` : 'auto';
    this.chart?.update();
  }

}
